import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ExternalLink, Bell, BellRing, Sparkles, Loader2, Zap, AlertTriangle, GitBranch, ArrowRight, Route as RouteIcon, FileText, ClipboardList, AlertOctagon, ShieldCheck, Plus, Mail, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";

export default function Alerts() {
  const { user, refresh } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null);
  const [drafting, setDrafting] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Manual alert entry (Admin / RA / QA Lead only)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    source: "", title: "", summary: "", link: "", severity: "medium", published: "",
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);

  // Digest preview (Admin only)
  const [digestPreview, setDigestPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // "Log as risk" (Admin / QA Lead / RA / Manufacturing)
  const [riskDialog, setRiskDialog] = useState(null); // {alert, device_id, severity, probability, initial}
  const [devices, setDevices] = useState([]);
  const [riskSubmitting, setRiskSubmitting] = useState(false);

  // Batch "Log as risk" — QA can tick several related alerts and log them
  // all against a single device in one round-trip.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchDialog, setBatchDialog] = useState(null); // {device_id, severity, probability, initial}
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  const navigate = useNavigate();

  const canPostManual = (user?.roles || []).some((r) => ["Admin", "RA", "QA Lead"].includes(r));
  const canLogRisk = (user?.roles || []).some((r) => ["Admin", "RA", "QA Lead", "Manufacturing"].includes(r));
  const isAdmin = (user?.roles || []).includes("Admin");
  const digestOn = !!user?.alert_digest_subscribed;
  const digestSignedOn = !!user?.alert_digest_signed_subscribed;

  const openDigestPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/admin/alerts/digest/preview");
      setDigestPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const openLogAsRisk = async (alert) => {
    // Lazy-load the device list only on first open so this doesn't slow
    // down the initial /alerts render.
    if (devices.length === 0) {
      try {
        const { data } = await api.get("/devices");
        setDevices(data);
        setRiskDialog({
          alert, device_id: data[0]?.project_id || "",
          severity: "Medium", probability: "Medium", initial: "Medium",
        });
      } catch (e) {
        toast.error("Could not load devices");
      }
    } else {
      setRiskDialog({
        alert, device_id: devices[0]?.project_id || "",
        severity: "Medium", probability: "Medium", initial: "Medium",
      });
    }
  };

  const submitRisk = async () => {
    if (!riskDialog?.device_id) {
      toast.error("Pick a device to bind this risk to");
      return;
    }
    setRiskSubmitting(true);
    try {
      const { data } = await api.post("/risks/from-alert", {
        alert_id: riskDialog.alert.alert_id,
        device_id: riskDialog.device_id,
        severity: riskDialog.severity,
        probability: riskDialog.probability,
        initial_risk_level: riskDialog.initial,
      });
      toast.success(
        data.reused
          ? "Alert already logged as a risk on that device — opening register"
          : "Risk added to register and linked to alert",
        { action: { label: "Open register", onClick: () => navigate("/risk-register") } },
      );
      setRiskDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Log as risk failed");
    } finally {
      setRiskSubmitting(false);
    }
  };

  const toggleSelect = (alertId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(alertId)) next.delete(alertId);
      else next.add(alertId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openBatchDialog = async () => {
    if (devices.length === 0) {
      try {
        const { data } = await api.get("/devices");
        setDevices(data);
        setBatchDialog({
          device_id: data[0]?.project_id || "",
          severity: "Medium", probability: "Medium", initial: "Medium",
        });
      } catch (e) {
        toast.error("Could not load devices");
      }
    } else {
      setBatchDialog({
        device_id: devices[0]?.project_id || "",
        severity: "Medium", probability: "Medium", initial: "Medium",
      });
    }
  };

  const submitBatch = async () => {
    if (!batchDialog?.device_id) {
      toast.error("Pick a device");
      return;
    }
    setBatchSubmitting(true);
    const ids = Array.from(selectedIds);
    let created = 0, reused = 0, failed = 0;
    const createdRiskIds = [];
    // Fire in parallel — /risks/from-alert is idempotent so double-submits
    // (e.g. accidental double-click) can't leak duplicates.
    await Promise.all(ids.map(async (aid) => {
      try {
        const { data } = await api.post("/risks/from-alert", {
          alert_id: aid,
          device_id: batchDialog.device_id,
          severity: batchDialog.severity,
          probability: batchDialog.probability,
          initial_risk_level: batchDialog.initial,
        });
        if (data.reused) reused++;
        else {
          created++;
          if (data?.risk?.risk_id) createdRiskIds.push(data.risk.risk_id);
        }
      } catch (e) {
        failed++;
      }
    }));
    setBatchSubmitting(false);
    setBatchDialog(null);
    clearSelection();
    // Reload alerts so newly-linked risk chips render immediately.
    load();
    // 30-second Undo — only offer if at least one new risk was created.
    // The toast disappears at 30s, which is our undo window.
    if (created > 0 && createdRiskIds.length > 0) {
      toast.success(
        `${created} risk${created === 1 ? "" : "s"} added · ${reused} already existed${failed ? ` · ${failed} failed` : ""}`,
        {
          duration: 30_000,
          action: {
            label: "Undo",
            onClick: () => undoBatch(createdRiskIds),
          },
        },
      );
    } else {
      toast.success(
        `${created} risk${created === 1 ? "" : "s"} added · ${reused} already existed${failed ? ` · ${failed} failed` : ""}`,
        { action: { label: "Open register", onClick: () => navigate("/risk-register") } },
      );
    }
  };

  const undoBatch = async (riskIds) => {
    try {
      const { data } = await api.post("/risks/batch-delete", { risk_ids: riskIds });
      // Reload alerts so the reverse-linked risk chips disappear immediately.
      load();
      if (data.deleted > 0) {
        toast.success(
          `Undone — ${data.deleted} risk${data.deleted === 1 ? "" : "s"} removed`,
        );
      } else {
        toast.error("Nothing was undone — the risks may have already been edited or the window has closed.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    }
  };

  const toggleDigest = async () => {
    try {
      await api.put("/auth/me/preferences", { alert_digest_subscribed: !digestOn });
      await refresh();
      toast.success(digestOn ? "Digest unsubscribed" : "Weekly digest turned on — first email arrives on the next Monday scan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preference update failed");
    }
  };

  const toggleSignedDigest = async () => {
    // Signed digest requires the base digest — offer to enable both together
    // rather than silently no-op so the user gets what they wanted.
    try {
      const wantOn = !digestSignedOn;
      const patch = { alert_digest_signed_subscribed: wantOn };
      if (wantOn && !digestOn) patch.alert_digest_subscribed = true;
      await api.put("/auth/me/preferences", patch);
      await refresh();
      if (wantOn) {
        toast.success(
          !digestOn
            ? "Weekly digest turned on with signed PDF — first email + signed attachment arrives Monday"
            : "Signed PDF attachment added to your Monday digest",
        );
      } else {
        toast.success("Signed PDF attachment turned off — you'll still get the plain digest");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preference update failed");
    }
  };

  const submitManualAlert = async () => {
    if (!manualForm.source || !manualForm.title) {
      toast.error("Source and title are required");
      return;
    }
    setManualSubmitting(true);
    try {
      const { data } = await api.post("/admin/alerts", manualForm);
      if (data.note) toast.info(data.note);
      else toast.success("Manual alert added to feed");
      setManualOpen(false);
      setManualForm({ source: "", title: "", summary: "", link: "", severity: "medium", published: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add alert");
    } finally {
      setManualSubmitting(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const analyze = async (alertId) => {
    setAnalyzing(alertId);
    try {
      await api.post(`/alerts/analyze-impact`, { alert_id: alertId }, { timeout: 45000 });
      toast.success("Impact analyzed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(null); }
  };

  const draftChange = async (alertId) => {
    setDrafting(alertId);
    try {
      const { data } = await api.post(`/alerts/draft-change-control`, { alert_id: alertId }, { timeout: 20000 });
      const chId = data.change_control?.change_id;
      const reused = data.reused;
      toast.success(reused ? "Draft change control already exists" : "Draft change control created", {
        action: chId ? { label: "Open", onClick: () => navigate("/changes") } : undefined,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally { setDrafting(null); }
  };

  const openTimeline = async (alertId) => {
    setLoadingTimeline(true);
    try {
      const { data } = await api.post(`/alerts/timeline`, { alert_id: alertId }, { timeout: 15000 });
      setTimeline(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Timeline load failed");
    } finally { setLoadingTimeline(false); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-5xl">
        <div className="vq-overline mb-2">Live feed • AI impact analysis</div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
            Regulatory Alerts
          </h1>
          <div className="flex items-center gap-2">
            <Button
              data-testid="alerts-digest-toggle"
              variant="outline"
              onClick={toggleDigest}
              className={`h-10 border-slate-300 font-semibold ${digestOn ? "bg-emerald-50 border-emerald-300 text-emerald-800" : ""}`}
              title={digestOn ? "You're subscribed to the weekly digest — click to unsubscribe" : "Get a Monday-morning email of the past week's regulatory news"}
            >
              <BellRing className="w-4 h-4 mr-1.5" />
              {digestOn ? "Digest ON" : "Weekly digest"}
            </Button>
            <Button
              data-testid="alerts-digest-signed-toggle"
              variant="outline"
              onClick={toggleSignedDigest}
              className={`h-10 border-slate-300 font-semibold ${digestSignedOn ? "bg-blue-50 border-blue-300 text-blue-800" : ""}`}
              title={digestSignedOn
                ? "You're receiving a Part-11 signed PDF alongside the digest — click to disable"
                : "Attach a Part-11-style signed PDF of the week's alerts to every digest, for audit-record"}
            >
              <ShieldCheck className="w-4 h-4 mr-1.5" />
              {digestSignedOn ? "Signed PDF ON" : "Signed PDF"}
            </Button>
            {isAdmin && (
              <Button
                data-testid="alerts-preview-digest"
                variant="outline"
                onClick={openDigestPreview}
                disabled={previewLoading}
                className="h-10 border-slate-300 font-semibold"
                title="Preview the HTML that Monday's digest email will send"
              >
                {previewLoading
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Loading…</>
                  : <><Mail className="w-4 h-4 mr-1.5" /> Preview digest</>}
              </Button>
            )}
            {canPostManual && (
              <Button
                data-testid="alerts-add-manual"
                onClick={() => setManualOpen(true)}
                className="h-10 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add alert
              </Button>
            )}
          </div>
        </div>
        <p className="text-slate-600 mb-8">Polled from FDA, EU EMA, MHRA (UK), TGA (Australia), Health Canada, ISO Standards and CDSCO (India) every 30 minutes. Click &ldquo;Analyze impact&rdquo; for AI-assisted assessment against your device portfolio.</p>

        {/* Add-manual dialog for sources without RSS/Atom (CDSCO circulars
             copied by hand, TÜV SÜD posts, MDCG guidance, private ISO announcements). */}
        <Dialog open={manualOpen} onOpenChange={setManualOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Add regulatory alert</DialogTitle>
              <DialogDescription>
                Use this to log updates from sources that don&apos;t publish RSS/Atom — e.g. PMDA notices, TÜV SÜD posts, private ISO announcements. Duplicates by (source, title) are silently reused.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Source *</Label>
                <Input data-testid="manual-alert-source" value={manualForm.source}
                       onChange={(e) => setManualForm((f) => ({ ...f, source: e.target.value }))}
                       className="mt-1.5" placeholder="TÜV SÜD, MDCG, PMDA, LinkedIn post…" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Title *</Label>
                <Input data-testid="manual-alert-title" value={manualForm.title}
                       onChange={(e) => setManualForm((f) => ({ ...f, title: e.target.value }))}
                       className="mt-1.5" placeholder="ISO 14155:2026 published — clinical investigations of medical devices" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Summary</Label>
                <Textarea data-testid="manual-alert-summary" value={manualForm.summary}
                          onChange={(e) => setManualForm((f) => ({ ...f, summary: e.target.value }))}
                          className="mt-1.5 h-24 text-sm" placeholder="What changed and who does it affect?" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Link</Label>
                  <Input data-testid="manual-alert-link" value={manualForm.link}
                         onChange={(e) => setManualForm((f) => ({ ...f, link: e.target.value }))}
                         className="mt-1.5 font-mono text-xs" placeholder="https://…" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Severity</Label>
                  <select
                    data-testid="manual-alert-severity"
                    value={manualForm.severity}
                    onChange={(e) => setManualForm((f) => ({ ...f, severity: e.target.value }))}
                    className="mt-1.5 w-full border border-slate-300 rounded h-9 px-2 text-sm bg-white"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
              <Button
                data-testid="manual-alert-submit"
                onClick={submitManualAlert}
                disabled={manualSubmitting}
                className="bg-slate-900 hover:bg-[#0055FF] text-white"
              >
                {manualSubmitting ? "Adding…" : "Add to feed"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {loading && <div className="text-slate-500">Loading alerts…</div>}

        <div className="space-y-3">
          {alerts.map((a) => (
            <div key={a.alert_id} data-testid={`alert-${a.alert_id}`} className="vq-card p-6 vq-card-hover">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                {canLogRisk && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(a.alert_id)}
                    onChange={() => toggleSelect(a.alert_id)}
                    data-testid={`alert-select-${a.alert_id}`}
                    aria-label="Select alert for batch log-as-risk"
                    className="w-4 h-4 cursor-pointer accent-[#0055FF]"
                  />
                )}
                <SeverityBadge severity={a.severity} />
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{a.source}</span>
                <span className="text-[10px] font-mono text-slate-400">•</span>
                <span className="text-[10px] font-mono text-slate-400">{a.published}</span>
                <TriageBadge triage={a.ai_triage} alertId={a.alert_id} />
                {a.impact_analysis && (
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-[#0055FF]/10 text-[#0055FF] border border-[#0055FF]/30">
                    ✓ Impact analyzed
                  </span>
                )}
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{a.title}</h3>
              <p className="text-sm text-slate-600 line-clamp-3 mb-3">{a.summary?.replace(/<[^>]+>/g, "")}</p>

              <LinkedRisksInline risks={a.linked_risks_summary || []} alertId={a.alert_id} />

              <div className="flex items-center gap-3 flex-wrap">
                {a.link && (
                  <a href={a.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[#0055FF] hover:underline">
                    Read source <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {!a.impact_analysis && (
                  <Button size="sm" variant="outline" onClick={() => analyze(a.alert_id)}
                          disabled={analyzing === a.alert_id}
                          data-testid={`analyze-impact-${a.alert_id}`}
                          className="border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white font-semibold">
                    {analyzing === a.alert_id ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Analyzing…</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Analyze impact</>}
                  </Button>
                )}
                {canLogRisk && (
                  <Button size="sm" variant="outline" onClick={() => openLogAsRisk(a)}
                          data-testid={`log-risk-${a.alert_id}`}
                          className="border-slate-300 text-slate-700 hover:border-[#0055FF] hover:text-[#0055FF] font-semibold"
                          title="One-tap: create a risk entry on a specific device and link it to this alert">
                    <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Log as risk
                  </Button>
                )}
              </div>

              {a.impact_analysis && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#0055FF]" />
                    <span className="vq-overline">AI impact assessment</span>
                    <span className={`ml-auto px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${
                      a.impact_analysis.risk_category === "urgent" ? "bg-red-50 text-red-700 border-red-200" :
                      a.impact_analysis.risk_category === "action_recommended" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`} data-testid={`risk-category-${a.alert_id}`}>{humanizeRiskCategory(a.impact_analysis.risk_category)}</span>
                  </div>
                  <p className="text-sm text-slate-700" data-testid={`impact-summary-${a.alert_id}`}>{a.impact_analysis.impact_summary}</p>
                  {(a.impact_analysis.affected_device_classes || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {a.impact_analysis.affected_device_classes.map((c, i) => (
                        <span key={`${c}-${i}`} className="text-[10px] font-mono border border-slate-200 rounded px-1.5 py-0.5 text-slate-700 bg-slate-50">{c}</span>
                      ))}
                    </div>
                  )}
                  {(a.impact_analysis.recommended_actions || []).length > 0 && (
                    <div className="text-xs text-slate-700">
                      <div className="vq-overline mb-1">Recommended actions</div>
                      <ul className="space-y-0.5 pl-4">
                        {a.impact_analysis.recommended_actions.map((r, i) => (<li key={`${a.alert_id}-act-${i}`} className="list-disc">{r}</li>))}
                      </ul>
                    </div>
                  )}
                  {(a.impact_analysis.affected_documents || []).length > 0 && (
                    <div className="text-xs text-slate-700">
                      <div className="vq-overline mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        Your Effective documents that may need review ({a.impact_analysis.affected_documents.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {a.impact_analysis.affected_documents.map((d) => (
                          <span key={d.document_id} className="text-[10px] font-mono border border-amber-200 bg-amber-50 rounded px-1.5 py-0.5 text-amber-800">
                            {d.title} v{d.version}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {a.impact_analysis._guardrails?.invalid_clauses?.length > 0 && (
                    <div className="text-[10px] font-mono text-amber-700 border-t border-slate-100 pt-2">
                      ⚠ {a.impact_analysis._guardrails.invalid_clauses.length} unverified clause reference(s) flagged — review before acting.
                    </div>
                  )}
                  <div className="pt-3 border-t border-slate-100 flex items-center gap-3 flex-wrap">
                    {a.change_control_id ? (
                      <Button size="sm" variant="ghost"
                              onClick={() => navigate("/changes")}
                              data-testid={`open-change-${a.alert_id}`}
                              className="text-emerald-700 hover:bg-emerald-50 font-semibold">
                        <GitBranch className="w-3.5 h-3.5 mr-1" />
                        Draft change control created
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    ) : (
                      <Button size="sm"
                              onClick={() => draftChange(a.alert_id)}
                              disabled={drafting === a.alert_id}
                              data-testid={`draft-change-${a.alert_id}`}
                              className="bg-[#0055FF] hover:bg-[#0044cc] text-white font-semibold">
                        {drafting === a.alert_id
                          ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Drafting…</>
                          : <><GitBranch className="w-3.5 h-3.5 mr-1" /> Draft change control</>}
                      </Button>
                    )}
                    <Button size="sm" variant="outline"
                            onClick={() => openTimeline(a.alert_id)}
                            data-testid={`view-timeline-${a.alert_id}`}
                            className="border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold">
                      <RouteIcon className="w-3.5 h-3.5 mr-1" /> Response timeline
                    </Button>
                    <span className="text-[10px] font-mono text-slate-400">
                      Pre-fills Change Control (Draft) with impact + actions. Human review + e-signature still required.
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {!loading && alerts.length === 0 && (
          <div className="vq-card p-10 text-center">
            <Bell className="w-8 h-8 text-slate-400 mx-auto mb-4" />
            <h3 className="font-display font-bold text-slate-900 mb-1">No alerts yet</h3>
            <p className="text-sm text-slate-500">Regulatory feeds are being polled. Check back soon.</p>
          </div>
        )}
      </div>

      {/* Log alert as risk — pick a device, review pre-fills, submit. */}
      <Dialog open={!!riskDialog} onOpenChange={(o) => { if (!o) setRiskDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="log-risk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#0055FF]" /> Log alert as risk
            </DialogTitle>
            <DialogDescription>
              A risk entry will be created on the selected device and linked to this alert. Hazard is pre-filled from the alert title; edit later on the register.
            </DialogDescription>
          </DialogHeader>
          {riskDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Alert</Label>
                <div className="text-sm text-slate-800 mt-0.5 line-clamp-2">{riskDialog.alert.title}</div>
                <div className="text-[10px] font-mono text-slate-500 uppercase">{riskDialog.alert.source}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold" htmlFor="risk-device">Device</Label>
                <select
                  id="risk-device"
                  value={riskDialog.device_id}
                  onChange={(e) => setRiskDialog({ ...riskDialog, device_id: e.target.value })}
                  className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                  data-testid="log-risk-device"
                >
                  {devices.length === 0 && <option value="">No devices — add one first</option>}
                  {devices.map((d) => (
                    <option key={d.project_id} value={d.project_id}>{d.device_name || d.project_id}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "severity", label: "Severity" },
                  { key: "probability", label: "Probability" },
                  { key: "initial", label: "Initial risk" },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <Label className="text-xs font-semibold">{label}</Label>
                    <select
                      value={riskDialog[key]}
                      onChange={(e) => setRiskDialog({ ...riskDialog, [key]: e.target.value })}
                      className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                      data-testid={`log-risk-${key}`}
                    >
                      {["Low", "Medium", "High"].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRiskDialog(null)}>Cancel</Button>
            <Button
              data-testid="log-risk-submit"
              onClick={submitRisk}
              disabled={riskSubmitting || !riskDialog?.device_id}
              className="bg-slate-900 hover:bg-[#0055FF] text-white"
            >
              {riskSubmitting ? "Logging…" : "Log as risk"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Digest Preview modal — Admin only. Shows the exact HTML that
          Monday's alert-digest email will send. Rendered in an iframe via
          srcDoc so the email's inline styles don't leak into the app. */}
      <Dialog open={!!digestPreview} onOpenChange={(o) => { if (!o) setDigestPreview(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#0055FF]" /> Digest email preview
            </DialogTitle>
            <DialogDescription>
              {digestPreview
                ? <>Week of <span className="font-semibold">{digestPreview.week_of}</span> · {digestPreview.alert_count} alert{digestPreview.alert_count === 1 ? "" : "s"} · subject line: <span className="font-mono text-xs">{digestPreview.subject}</span></>
                : "Loading…"}
            </DialogDescription>
          </DialogHeader>
          {digestPreview && (
            <div className="border border-slate-200 rounded bg-white overflow-hidden" data-testid="digest-preview-iframe-wrap">
              <iframe
                title="Digest preview"
                srcDoc={digestPreview.html}
                sandbox=""
                className="w-full h-[60vh]"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDigestPreview(null)} data-testid="digest-preview-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regulatory Response Timeline dialog */}
      <Dialog open={!!timeline || loadingTimeline} onOpenChange={(o) => { if (!o) setTimeline(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="timeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <RouteIcon className="w-5 h-5 text-[#0055FF]" /> Regulatory Response Timeline
            </DialogTitle>
            <DialogDescription>
              Single-shot inspector artefact: alert → impact analysis → change control → downstream actions and evidence.
            </DialogDescription>
          </DialogHeader>
          {loadingTimeline && <div className="flex items-center gap-2 text-slate-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Building timeline…</div>}
          {timeline && !loadingTimeline && <TimelineView t={timeline} />}
        </DialogContent>
      </Dialog>

      {/* Sticky selection bar — appears when >=1 alert is ticked, offers
          batch Log-as-risk. Positioned fixed so it stays visible while the
          user scrolls the alerts feed. */}
      {selectedIds.size > 0 && canLogRisk && (
        <div
          data-testid="alerts-selection-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-900 text-white rounded-full shadow-xl px-4 py-2"
        >
          <span data-testid="alerts-selected-count" className="text-sm font-semibold">
            {selectedIds.size} selected
          </span>
          <div className="w-px h-4 bg-slate-700" />
          <Button
            size="sm"
            onClick={openBatchDialog}
            data-testid="alerts-batch-log-risk-btn"
            className="bg-[#0055FF] hover:bg-[#0044cc] text-white font-semibold h-8"
          >
            <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
            Log as risks
          </Button>
          <button
            onClick={clearSelection}
            data-testid="alerts-clear-selection"
            className="p-1 rounded hover:bg-slate-700"
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Batch log-as-risk dialog — same shape as single dialog but applied
          to N alerts at once. Submit is parallelised (Promise.all) and
          duplicate-safe (endpoint is idempotent per alert_id+device_id). */}
      <Dialog open={!!batchDialog} onOpenChange={(o) => { if (!o) setBatchDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="batch-log-risk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#0055FF]" /> Log {selectedIds.size} alert{selectedIds.size === 1 ? "" : "s"} as risks
            </DialogTitle>
            <DialogDescription>
              A risk will be created on the selected device for each alert and linked back to it. Already-logged (alert_id, device_id) pairs are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          {batchDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold" htmlFor="batch-risk-device">Device</Label>
                <select
                  id="batch-risk-device"
                  value={batchDialog.device_id}
                  onChange={(e) => setBatchDialog({ ...batchDialog, device_id: e.target.value })}
                  className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                  data-testid="batch-log-risk-device"
                >
                  {devices.length === 0 && <option value="">No devices — add one first</option>}
                  {devices.map((d) => (
                    <option key={d.project_id} value={d.project_id}>{d.device_name || d.project_id}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "severity", label: "Severity" },
                  { key: "probability", label: "Probability" },
                  { key: "initial", label: "Initial risk" },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <Label className="text-xs font-semibold">{label}</Label>
                    <select
                      value={batchDialog[key]}
                      onChange={(e) => setBatchDialog({ ...batchDialog, [key]: e.target.value })}
                      className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                      data-testid={`batch-log-risk-${key}`}
                    >
                      {["Low", "Medium", "High"].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-[10px] font-mono text-slate-500">
                Each alert&apos;s title is used as the risk hazard. Edit individually on the register afterwards.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchDialog(null)}>Cancel</Button>
            <Button
              data-testid="batch-log-risk-submit"
              onClick={submitBatch}
              disabled={batchSubmitting || !batchDialog?.device_id}
              className="bg-slate-900 hover:bg-[#0055FF] text-white"
            >
              {batchSubmitting ? `Logging ${selectedIds.size}…` : `Log ${selectedIds.size} as risks`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function TimelineView({ t }) {
  const rows = [];
  const alert = t.alert || {};
  rows.push({
    key: `alert-${alert.alert_id || "root"}`,
    icon: <Bell className="w-4 h-4 text-slate-500" />,
    when: alert.published || alert.created_at,
    title: alert.title || "Regulatory alert",
    body: (
      <>
        <div className="text-xs text-slate-500 font-mono">{alert.source} · {alert.severity}</div>
        <p className="text-sm text-slate-700 mt-1 line-clamp-3">{(alert.summary || "").replace(/<[^>]+>/g, "")}</p>
      </>
    ),
  });
  if (t.impact_analysis) {
    rows.push({
      key: "impact-analysis",
      icon: <Zap className="w-4 h-4 text-[#0055FF]" />,
      when: null,
      title: `AI impact assessment — ${humanizeRiskCategory(t.impact_analysis.risk_category)}`,
      body: (
        <>
          <p className="text-sm text-slate-700">{t.impact_analysis.impact_summary}</p>
          {(t.impact_analysis.recommended_actions || []).length > 0 && (
            <ul className="text-xs text-slate-700 mt-2 pl-4 list-disc space-y-0.5">
              {t.impact_analysis.recommended_actions.slice(0, 5).map((r, i) => <li key={`timeline-act-${i}`}>{r}</li>)}
            </ul>
          )}
        </>
      ),
    });
  }
  if (t.change_control) {
    const cc = t.change_control;
    rows.push({
      key: `cc-${cc.change_id}`,
      icon: <GitBranch className="w-4 h-4 text-emerald-600" />,
      when: cc.created_at,
      title: `Change Control — ${cc.state} · ${cc.risk_level} risk`,
      body: (
        <>
          <div className="text-xs font-mono text-slate-500">{cc.change_id}</div>
          <div className="font-semibold text-sm text-slate-900 mt-0.5">{cc.title}</div>
        </>
      ),
    });
  }
  (t.change_audit_events || []).forEach((e, i) => {
    rows.push({
      key: `audit-${e.timestamp || i}-${e.action}`,
      icon: <ShieldCheck className="w-4 h-4 text-slate-400" />,
      when: e.timestamp,
      title: `Audit · ${e.action}`,
      body: <div className="text-xs text-slate-600">{e.user_email}{e.reason ? ` — ${e.reason}` : ""}</div>,
    });
  });
  (t.change_signatures || []).forEach((s, i) => {
    rows.push({
      key: `sig-${s.signature_id || s.signed_at || i}`,
      icon: <ShieldCheck className="w-4 h-4 text-emerald-600" />,
      when: s.signed_at,
      title: `E-signature · ${s.meaning}`,
      body: <div className="text-xs text-slate-600">{s.signer_email} — &ldquo;{s.reason}&rdquo;</div>,
    });
  });
  (t.linked_deviations || []).forEach((d) => {
    rows.push({
      key: `dev-${d.deviation_id || d.title}`,
      icon: <AlertOctagon className="w-4 h-4 text-amber-600" />,
      when: d.created_at,
      title: `Deviation · ${d.state}`,
      body: <div className="text-sm text-slate-700">{d.title}</div>,
    });
  });
  (t.linked_capas || []).forEach((c) => {
    rows.push({
      key: `capa-${c.capa_id || c.title}`,
      icon: <ClipboardList className="w-4 h-4 text-amber-600" />,
      when: c.created_at,
      title: `CAPA · ${c.state}`,
      body: <div className="text-sm text-slate-700">{c.title}</div>,
    });
  });
  (t.linked_documents || []).forEach((d) => {
    rows.push({
      key: `doc-${d.document_id || d.title}`,
      icon: <FileText className="w-4 h-4 text-slate-500" />,
      when: d.updated_at,
      title: `Document · ${d.state} · v${d.version}`,
      body: <div className="text-sm text-slate-700">{d.title}</div>,
    });
  });

  const empty = rows.length <= 1 && !t.change_control && !t.impact_analysis;

  return (
    <div className="space-y-4 pt-2">
      <div className="text-[10px] font-mono text-slate-400">
        Generated {new Date(t.generated_at).toLocaleString()} by {t.generated_by}
      </div>
      {empty && (
        <div className="text-sm text-slate-500 vq-card p-6 text-center">
          No downstream response yet. Run impact analysis, then draft a change control to populate the timeline.
        </div>
      )}
      <div className="relative pl-6 border-l-2 border-slate-200 space-y-4">
        {rows.map((r, i) => (
          <div key={r.key || `row-${i}`} className="relative" data-testid={`timeline-row-${i}`}>
            <div className="absolute -left-[30px] w-6 h-6 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
              {r.icon}
            </div>
            <div className="vq-card p-4">
              <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
                <div className="font-semibold text-sm text-slate-900">{r.title}</div>
                {r.when && <div className="text-[10px] font-mono text-slate-400">{new Date(r.when).toLocaleString()}</div>}
              </div>
              {r.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const cls = severity === "high" ? "vq-badge-high" : severity === "medium" ? "vq-badge-med" : "vq-badge-low";
  return <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${cls}`}>{severity}</span>;
}

/**
 * LinkedRisksInline — small chip strip showing the risks already linked to
 * this alert. Feeds off `alert.linked_risks_summary` (batch-hydrated in
 * routes/alerts.py so it costs one $in per page, not N lookups). Renders
 * nothing when there are no linked risks — keeps the card clean.
 */
function LinkedRisksInline({ risks, alertId }) {
  if (!risks || risks.length === 0) return null;
  const levelCls = (lvl) => (
    lvl === "High" ? "bg-red-100 text-red-800 border-red-300"
    : lvl === "Medium" ? "bg-amber-100 text-amber-800 border-amber-300"
    : lvl === "Low" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : "bg-slate-100 text-slate-700 border-slate-300"
  );
  return (
    <div
      data-testid={`alert-linked-risks-${alertId}`}
      className="mb-3 border-l-2 border-l-slate-200 pl-3"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        <ShieldAlert className="w-3 h-3" /> Linked risks · {risks.length}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {risks.map((r) => (
          <a
            key={r.risk_id}
            href="/risk-register"
            data-testid={`alert-linked-risk-${alertId}-${r.risk_id}`}
            className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
            title={`${r.title} · ${r.device_name || r.device_id} · state ${r.state}`}
          >
            <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${levelCls(r.residual_risk_level || r.initial_risk_level)}`}>
              {r.residual_risk_level || r.initial_risk_level || "?"}
            </span>
            <span className="truncate max-w-[180px]">{r.title}</span>
            <span className="text-[9px] font-mono text-slate-400">· {r.device_name || r.device_id}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * AI triage badge — shows the portfolio-relevance score returned by
 * Claude Haiku 4.5. Score buckets:
 *   80-100 emerald "High relevance"
 *   50-79  blue    "Relevant"
 *   20-49  slate   "Informational"
 *   0-19   muted   "Low relevance"
 * A tooltip surfaces the AI rationale + category so users can gauge
 * why a given alert scored where it did.
 */
function TriageBadge({ triage, alertId }) {
  if (!triage || typeof triage.relevance_score !== "number") return null;
  const s = triage.relevance_score;
  let cls, label;
  if (s >= 80) { cls = "bg-emerald-50 text-emerald-800 border-emerald-300"; label = "High relevance"; }
  else if (s >= 50) { cls = "bg-blue-50 text-blue-800 border-blue-300"; label = "Relevant"; }
  else if (s >= 20) { cls = "bg-slate-100 text-slate-700 border-slate-300"; label = "Informational"; }
  else { cls = "bg-slate-50 text-slate-500 border-slate-200"; label = "Low relevance"; }
  const tip = `${label} · ${triage.category || "Other"}${triage.rationale ? "\n" + triage.rationale : ""}`;
  return (
    <span
      data-testid={`alert-triage-${alertId}`}
      title={tip}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}
    >
      <Sparkles className="w-3 h-3" /> AI {s} · {triage.category || "Other"}
    </span>
  );
}

function humanizeRiskCategory(cat) {
  if (!cat) return "";
  const map = {
    urgent: "Urgent",
    action_recommended: "Action Recommended",
    informational: "Informational",
  };
  return map[cat] || cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
